<script setup lang="ts">
  import { VRadio } from "@krainovsd/vue-ui";
  import { computed, ref } from "vue";
  import type { ComparisonMethod } from "@/entities/comparison";
  import Card from "@/components/atoms/Card.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const method = defineModel<ComparisonMethod>({ required: true });
  const emit = defineEmits<{ next: []; back: []; submit: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  // Bayesian has no indicators step — it submits straight from here; factor
  // continues to the indicators step.
  const isBayesian = computed(() => method.value === "bayesian");

  const METHOD_OPTIONS: { value: ComparisonMethod; title: string; description: string }[] = [
    {
      value: "factor",
      title: "Факторный анализ",
      description: "Дельты P1/P2/Δ/Δ% по всем 11 метрикам и 6–7 разрезам.",
    },
    {
      value: "bayesian",
      title: "Байесовский анализ",
      description: "P(A > B), постериорные распределения, credible interval.",
    },
  ];
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="$t('page.createComparison.step4.title', {}, { default: 'Выберите метод' })"
    :subtitle="
      $t(
        'page.createComparison.step4.subtitle',
        {},
        { default: 'Чем считать различие — точечной арифметикой или распределениями' },
      )
    "
    back
    :next="!isBayesian"
    :submit="isBayesian"
    :submit-label="$t('page.createComparison.create', {}, { default: 'Создать сравнение' })"
    @back="emit('back')"
    @next="emit('next')"
    @submit="emit('submit')"
  >
    <div :class="$style.methods">
      <Card
        v-for="option in METHOD_OPTIONS"
        :key="option.value"
        :class="$style.methodCard"
        @click="method = option.value"
      >
        <VRadio
          v-model="method"
          :name="option.title"
          :value="option.value"
          :class="$style.methodRadio"
        />
        <div :class="$style.methodContent">
          <span :class="$style.methodTitle">{{ option.title }}</span>
          <span :class="$style.methodDescription">{{ option.description }}</span>
        </div>
      </Card>
    </div>
  </StepLayout>
</template>

<style lang="scss" module>
  .methods {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .methodCard {
    padding: 14px;

    display: flex;
    align-items: flex-start;

    cursor: pointer;
  }

  .methodRadio {
    margin-top: 2px;
  }

  .methodContent {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .methodTitle {
    color: var(--foreground);
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .methodDescription {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.375;
  }
</style>
